import React, { useId, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, UserProfile } from '../services/api';
import { useGoogleSignIn } from '../hooks/useGoogleSignIn';
import { MagicEmailAuth } from './MagicEmailAuth';
import { useCompleteSignIn } from '../hooks/useCompleteSignIn';

type Mode = 'login' | 'register' | 'reset' | 'verify' | 'password' | 'resend';
interface Props { onProfileUpdate: (profile: UserProfile) => void; onSuccess?: () => void; }
const button = 'w-full rounded-md bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50';
const input = 'mt-1 w-full rounded-md border border-ink-200 bg-surface px-3 py-2 text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500';

const GoogleIcon = () => (
  <svg aria-hidden="true" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
);

function errorMessage(error: any): string {
  if (error.response?.status === 429) return 'Too many attempts. Please try again later.';
  const data = error.response?.data;
  if (!data) return 'We couldn’t connect. Please try again.';
  const messages: string[] = Object.values(data).flatMap(value => typeof value === 'string' ? [value] : Array.isArray(value) ? value : []);
  return messages.join(' ') || 'Something went wrong. Please try again.';
}

export const EmailAuthForm = ({ onProfileUpdate, onSuccess, initialMode = 'login', verificationKey, uid, token }: Props & {
  initialMode?: Mode; verificationKey?: string; uid?: string; token?: string;
}) => {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(value => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  const cooldownFromError = (err: any) => {
    if (err.response?.status === 429) {
      const seconds = Number(err.response.headers?.['retry-after']);
      setCooldown(Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 60);
    }
  };
  const id = useId();
  const completeSignIn = useCompleteSignIn(onProfileUpdate, onSuccess);
  const titles = { login: 'Sign in with email', register: 'Create your account', reset: 'Reset your password', verify: 'Verify your email', password: 'Choose a new password', resend: 'Verify your email' };

  const switchMode = (next: Mode) => { setMode(next); setError(''); setNotice(''); setPassword(''); setAwaitingVerification(false); };
  const resend = async () => {
    if (inFlight.current || cooldown) return;
    inFlight.current = true;
    setBusy(true); setError('');
    try {
      await api.post('/auth/email/resend/', { email: email.trim() });
      setCooldown(60);
      setNotice('If this email needs verification, a new link is on its way. Check your inbox and spam folder.');
    } catch (err) { cooldownFromError(err); setError(errorMessage(err)); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (inFlight.current || (cooldown > 0 && ['register', 'reset', 'resend'].includes(mode))) return;
    inFlight.current = true; setBusy(true); setError(''); setNotice('');
    try {
      if (mode === 'login') {
        const response = await api.post('/auth/email/login/', { email, password });
        localStorage.removeItem('googleFirstName');
        await completeSignIn(response.data.key, 'email');
      } else if (mode === 'register') {
        await api.post('/auth/email/register/', { email, password1: password, password2: password });
        setCooldown(60); setPassword(''); setMode('login'); setAwaitingVerification(true);
        setNotice('We sent you a verification link. Open it to finish creating your account and sign in.');
      } else if (mode === 'resend') {
        await api.post('/auth/email/resend/', { email: email.trim() });
      setCooldown(60);
        setNotice('If this email needs verification, a new link is on its way. Check your inbox and spam folder.');
      } else if (mode === 'reset') {
        await api.post('/auth/email/reset/', { email: email.trim() });
        setCooldown(60);
        setNotice('If an account exists for this email, you’ll receive a password reset link shortly.');
      } else if (mode === 'verify') {
        const response = await api.post('/auth/email/verify/', { key: verificationKey });
        localStorage.removeItem('googleFirstName');
        await completeSignIn(response.data.key, 'email', response.data.is_new_account === true);
      } else {
        const response = await api.post('/auth/email/reset/confirm/', { uid, token, new_password1: password, new_password2: password });
        setPassword('');
        if (response.data.key) {
          localStorage.removeItem('googleFirstName');
          await completeSignIn(response.data.key, 'email');
        } else {
          setEmail(response.data.email || ''); setMode('login'); setAwaitingVerification(true);
          setNotice('Password saved. Verify your email to finish signing in. You can request a new verification link below.');
        }
      }
    } catch (err: any) {
      cooldownFromError(err);
      if (mode === 'register' && err.response?.data?.code === 'verification_pending') {
        setMode('login'); setPassword(''); setAwaitingVerification(true);
        setNotice('Your account is waiting for email verification. Check your inbox, or resend the link below.');
      } else if (mode === 'register' && err.response?.data?.code === 'account_exists') {
        setMode('login'); setPassword('');
        setExistingAccount(true);
      } else if (mode === 'verify' && err.response?.status === 404) setError('This verification link has expired or already been used. You may already be verified. Use a new email sign-in link below.');
      else if (mode === 'password' && (err.response?.data?.token || err.response?.data?.uid)) setError('This reset link is invalid or has expired. Request a new one below.');
      else setError(errorMessage(err));
    }
    finally { inFlight.current = false; setBusy(false); }
  };

  if (existingAccount) return <MagicEmailAuth initialEmail={email} existingAccount onProfileUpdate={onProfileUpdate} onSuccess={onSuccess} onPassword={() => setExistingAccount(false)} />;

  return <div className="space-y-5">
    <h2 id={`${id}-title`} className="font-display text-2xl tracking-tight text-ink-900">{awaitingVerification ? 'Check your email' : titles[mode]}</h2>
    {notice && <p role="status" className="text-sm text-ink-700">{notice}</p>}
    {notice && process.env.REACT_APP_ENVIRONMENT === 'staging' && <p className="text-sm text-ink-700">
      Testing on preview? <a href="/_staging/inbox/" target="_blank" rel="noreferrer" className="font-medium text-brand-700 underline">Open test emails</a> for your verification or password reset link.
    </p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {!awaitingVerification && <form onSubmit={submit} className="space-y-4" aria-label={titles[mode]}>
      {!['verify', 'password'].includes(mode) && <label htmlFor={`${id}-email`} className="block text-sm text-ink-700">Email
        <input id={`${id}-email`} type="email" autoComplete="email" required maxLength={254} value={email} onChange={e => setEmail(e.target.value)} className={input} />
      </label>}
      {['login', 'register', 'password'].includes(mode) && <div>
        <label htmlFor={`${id}-password`} className="block text-sm text-ink-700">{mode === 'password' ? 'New password' : 'Password'}</label>
        <div className="relative">
          <input id={`${id}-password`} type={showPassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required maxLength={128} minLength={mode === 'login' ? undefined : 8} value={password} onChange={e => setPassword(e.target.value)} className={`${input} pr-16`} />
          <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-sm text-ink-600">{showPassword ? 'Hide' : 'Show'}</button>
        </div>
        {mode !== 'login' && <p className="mt-1 text-xs text-ink-500">Use at least 8 characters. Avoid common passwords.</p>}
      </div>}
      {mode === 'reset' && <p className="text-sm text-ink-600">Joined with Google? You can also set a password here and keep the same account.</p>}
      {mode === 'verify' && <p className="text-sm text-ink-600">Confirm your email to finish creating your account.</p>}
      <button disabled={busy || (cooldown > 0 && ['register', 'reset', 'resend'].includes(mode))} className={button}>{cooldown > 0 && ['register', 'reset', 'resend'].includes(mode) ? `Try again in ${cooldown}s` : busy ? 'Please wait…' : { login: 'Sign in', register: 'Create account', reset: 'Send reset link', verify: 'Verify and sign in', password: 'Save password', resend: 'Send verification link' }[mode]}</button>
    </form>}
    {mode === 'login' && !awaitingVerification && <div className="space-y-3 text-center text-sm">
      <button disabled={busy} onClick={() => switchMode('reset')} className="text-ink-600 hover:underline">Forgot password?</button>
      <p className="text-ink-600">New to Eatsy? <button disabled={busy} onClick={() => switchMode('register')} className="font-medium text-brand-700 hover:underline">Create account</button></p>

    </div>}
    {awaitingVerification && <>
      <p className="text-sm text-ink-600">{email} · Check your spam folder too.</p>
      <button disabled={busy} onClick={() => switchMode('login')} className="w-full text-sm text-brand-700 hover:underline">Back to sign in</button>
    </>}
    {(awaitingVerification || /verif/i.test(error)) && !['verify', 'resend'].includes(mode) && <button disabled={busy || !email || cooldown > 0} onClick={resend} className="w-full text-sm text-brand-700 hover:underline">{cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend verification email'}</button>}
    {mode === 'password' && error && <button disabled={busy} onClick={() => switchMode('reset')} className="w-full text-sm text-brand-700 hover:underline">Request a new reset link</button>}
    {mode === 'verify' && error && <a href="/auth/email" className="block text-center text-sm text-brand-700 hover:underline">Get a new sign-in link</a>}
    {['register', 'reset', 'resend'].includes(mode) && <button disabled={busy} onClick={() => switchMode('login')} className="w-full text-sm text-brand-700 hover:underline">Back to sign in</button>}
  </div>;
};

export const SignInButton = ({ label = 'Sign in with', ...props }: Props & { label?: string }) => {
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [error, setError] = useState('');
  const complete = () => { setOpen(false); props.onSuccess?.(); };
  const googleLogin = useGoogleSignIn({ onProfileUpdate: props.onProfileUpdate, onSuccess: complete, onError: setError });
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.querySelector<HTMLElement>('[data-initial-focus]')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); }
      if (event.key !== 'Tab') return;
      const elements = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href]');
      if (!elements?.length) return;
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', keydown); previousFocus?.focus(); };
  }, [open]);
  useEffect(() => {
    if (open) dialog.current?.querySelector<HTMLElement>(emailOpen ? 'input' : '[data-initial-focus]')?.focus();
  }, [open, emailOpen]);
  return <>
    <button aria-label={label === 'Sign in with' ? 'Sign in with Google or email' : label} onClick={() => { setEmailOpen(false); setPasswordOpen(false); setError(''); setOpen(true); }} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700 sm:w-auto">
      <span>{label}</span>
      <span aria-hidden="true" className="inline-flex items-center gap-2">
        <GoogleIcon />
        <Mail className="h-4 w-4 shrink-0" />
      </span>
    </button>
    {open && createPortal(<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div ref={dialog} role="dialog" aria-modal="true" aria-label="Sign in" className="relative max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-xl bg-surface p-6 shadow-xl">
        <button aria-label="Close sign in" onClick={() => setOpen(false)} className="absolute right-3 top-2 px-2 py-1 text-xl text-ink-500">×</button>
        {emailOpen ? <>
          {passwordOpen ? <><EmailAuthForm {...props} onSuccess={complete} /><button onClick={() => setPasswordOpen(false)} className="mt-4 w-full text-sm text-brand-700 hover:underline">Use a magic sign-in link instead</button></> : <MagicEmailAuth {...props} onSuccess={complete} onPassword={() => setPasswordOpen(true)} />}
          <button onClick={() => setEmailOpen(false)} className="mt-4 w-full text-sm text-ink-600 hover:underline">Other sign-in options</button>
        </> : <div className="space-y-5">
          <h2 className="font-display text-2xl tracking-tight text-ink-900">Welcome to Eatsy</h2>
          <p className="text-sm text-ink-600">Sign in or create an account to save your favourites.</p>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="space-y-3">
            <button data-initial-focus onClick={() => { setError(''); googleLogin(); }} className="flex w-full items-center justify-center gap-2 rounded-md border border-ink-200 px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-brand-50">
              <GoogleIcon />
              Continue with Google
            </button>
            <button onClick={() => { setPasswordOpen(false); setEmailOpen(true); }} className={button}>Continue with email</button>
          </div>
        </div>}
      </div>
    </div>, document.body)}
  </>;
};

export const EmailAuthPage = ({ onProfileUpdate }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const magicToken = new URLSearchParams(location.hash.slice(1)).get('magic') || undefined;
  const query = new URLSearchParams(location.search);
  const verificationKey = query.get('key') || undefined;
  const uid = query.get('uid') || undefined;
  const token = query.get('token') || undefined;
  return <div className="mx-auto my-12 max-w-sm rounded-xl bg-surface p-6 shadow-sm">
    {!verificationKey && !(uid && token) && !passwordOpen ? <MagicEmailAuth key={location.hash} magicToken={magicToken} onProfileUpdate={onProfileUpdate} onPassword={() => setPasswordOpen(true)} onSuccess={() => navigate('/availability', { replace: true })} /> : <EmailAuthForm key={location.search} initialMode={verificationKey ? 'verify' : uid && token ? 'password' : 'login'} verificationKey={verificationKey} uid={uid} token={token} onProfileUpdate={onProfileUpdate} onSuccess={() => navigate('/availability', { replace: true })} />}
    {passwordOpen && !verificationKey && !(uid && token) && <button onClick={() => setPasswordOpen(false)} className="mt-4 w-full text-sm text-brand-700 hover:underline">Use a magic sign-in link instead</button>}
  </div>;
};
